import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  LinearProgress,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
  Badge,
  Divider,
  Fade
} from '@mui/material';
import {
  PowerSettingsNew,
  TrendingUp,
  TrendingDown,
  Schedule,
  Speed,
  Refresh,
  CheckCircle,
  Warning,
  Error,
  LocalGasStation,
  Euro,
  Timer,
  Engineering,
  PlayArrow,
  Pause,
  ShowChart,
  AutoGraph,
  Factory,
  Info
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { isMultiTenantMode } from '../config/api';
import moment from 'moment-timezone';

// Configureer moment voor Nederlandse locale
moment.locale('nl');
moment.tz.setDefault('Europe/Amsterdam');

// AnimatedNumber component voor mooie counter animaties
const AnimatedNumber = ({ value, format = 'number', decimals = 0, prefix = '', suffix = '', duration = 800 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    // Parse de waarde
    const newValue = parseFloat(value) || 0;
    const oldValue = displayValue;
    
    // Bepaal aantal stappen
    const steps = 20;
    const increment = (newValue - oldValue) / steps;
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayValue(newValue);
        clearInterval(timer);
      } else {
        setDisplayValue(oldValue + (increment * currentStep));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  // Format het getal
  const formatValue = () => {
    if (format === 'currency') {
      return `${prefix}€ ${displayValue.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
    } else if (format === 'percentage') {
      return `${prefix}${displayValue.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%${suffix}`;
    } else {
      return `${prefix}${displayValue.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
    }
  };
  
  return <span>{formatValue()}</span>;
};

// ExtrapolatedNumber component voor vloeiende realtime updates
const ExtrapolatedNumber = ({ movingAverage, fallbackValue = 0, format = 'number', decimals = 0, prefix = '', suffix = '', duration = 800, canDecrease = false }) => {
  // Als moving average nog geen current heeft, gebruik de fallback (actuele waarde)
  const initialValue = movingAverage?.current !== undefined ? movingAverage.current : fallbackValue;
  const [displayValue, setDisplayValue] = useState(initialValue);
  const intervalRef = useRef(null);
  const lastUpdateRef = useRef(moment());
  const velocityRef = useRef(0);
  
  // Update displayValue wanneer nieuwe data binnenkomt
  useEffect(() => {
    if (!movingAverage) {
      return; // Wacht op data
    }
    
    if (movingAverage.current === undefined) {
      // Nog geen data, gebruik fallback maar start geen animatie
      if (fallbackValue !== displayValue) {
        setDisplayValue(fallbackValue);
      }
      return;
    }
    
    // Clear bestaande interval - stop alle animaties tijdens update
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Update velocity reference
    velocityRef.current = movingAverage.smoothedVelocity || 0;
    lastUpdateRef.current = moment();
    
    // Start waarden
    const currentDisplay = displayValue;
    const targetValue = movingAverage.current;
    
    // Bij eerste keer laden of wanneer we van undefined komen: direct naar waarde
    const isFirstRealData = !movingAverage.hasFirstUpdate && targetValue !== undefined;
    
    if (isFirstRealData) {
      setDisplayValue(targetValue);
    }
    
    // Bereken de ideale velocity op basis van het verschil
    const timeSinceLastUpdate = moment().diff(lastUpdateRef.current, 'seconds', true) || 1;
    const diff = targetValue - currentDisplay;
    
    // Gebruik de velocity van de moving average (physics-based)
    if (movingAverage.smoothedVelocity !== undefined) {
      velocityRef.current = movingAverage.smoothedVelocity;
      
      // Debug velocity updates
      if (Math.abs(diff) > 0.01 && velocityRef.current !== 0) {
        let description = '';
        let displayVelocity = velocityRef.current;
        
        // Maak beschrijving op basis van type counter
        if (format === 'currency') {
          displayVelocity = velocityRef.current * 3600;
          description = `€${displayVelocity.toFixed(2)}/uur`;
        } else if (format === 'percentage') {
          displayVelocity = velocityRef.current * 3600;
          description = `${displayVelocity.toFixed(2)}%/uur`;
        } else if (suffix && suffix.includes('kW')) {
          description = `${displayVelocity.toFixed(1)} kW/sec`;
        } else {
          displayVelocity = velocityRef.current * 60;
          description = `${displayVelocity.toFixed(2)}/min`;
        }
      }
    }
    
        // Start constante lineaire extrapolatie
    const startExtrapolation = () => {
      // Start altijd, ook met velocity 0 (dan staat counter stil)
      const updateFrequency = 100; // Update elke 100ms
      
      intervalRef.current = setInterval(() => {
        setDisplayValue(prev => {
          // Als velocity 0 is, blijf op huidige waarde
          if (Math.abs(velocityRef.current) < 0.000001) {
            return prev;
          }
          
          // Bereken increment voor deze update (0.1 seconde)
          const timeStep = updateFrequency / 1000; // 0.1 seconde
          const increment = velocityRef.current * timeStep;
          let newValue = prev + increment;
          
          // Voorkom dat counters teruglopen (tenzij toegestaan)
          if (!canDecrease && increment < 0 && newValue < prev) {
            return prev; // Blijf op huidige waarde
          }
          
          // Voorkom negatieve waarden voor non-currency counters
          if (!canDecrease && newValue < 0) {
            return 0;
          }
          
          return newValue;
        });
      }, updateFrequency);
    };
    
    // Start extrapolatie direct
    startExtrapolation();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
      }, [movingAverage, fallbackValue, canDecrease, suffix]);
  
  // Format het getal
  const formatValue = () => {
    const value = displayValue || 0;
    if (format === 'currency') {
      return `${prefix}€ ${value.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
    } else if (format === 'percentage') {
      return `${prefix}${value.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%${suffix}`;
    } else {
      return `${prefix}${value.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
    }
  };
  
  return <span>{formatValue()}</span>;
};

// AnimatedProgress component voor vloeiende progress bar animaties
const AnimatedProgress = ({ value, sx, ...props }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  
  useEffect(() => {
    // Parse de waarde
    const newValue = Math.min(Math.max(parseFloat(value) || 0, 0), 100);
    const oldValue = animatedValue;
    
    // Bepaal aantal stappen voor vloeiende animatie
    const steps = 30;
    const increment = (newValue - oldValue) / steps;
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setAnimatedValue(newValue);
        clearInterval(timer);
      } else {
        setAnimatedValue(oldValue + (increment * currentStep));
      }
    }, 600 / steps); // 600ms totale animatie tijd
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <LinearProgress 
      {...props}
      value={animatedValue}
      sx={{
        ...sx,
        transition: 'none', // Disable MUI's eigen transition want we animeren zelf
        '& .MuiLinearProgress-bar': {
          transition: 'none',
          ...sx?.['& .MuiLinearProgress-bar']
        }
      }}
    />
  );
};

const DashboardPage = () => {
  const { selectedEnvironment } = useAuth();
  const { get } = useApi();
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(moment());
  
  // UI state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5); // Start met 5 seconden
  const [compactView, setCompactView] = useState(false);
  const [sessionStartTime] = useState(moment()); // Track wanneer de sessie begon
  const [lastUserInteraction, setLastUserInteraction] = useState(moment()); // Track laatste user interactie
  
  // Raw data states
  const [relaisData, setRelaisData] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [configData, setConfigData] = useState({});
  const [overrideStatus, setOverrideStatus] = useState(null); // Nieuwe state voor override status
  
  // Settings states (zoals Planning pagina)
  const [electricitySettings, setElectricitySettings] = useState({ 
    show_all_in: true,
    netbeheerder_tarief: 0.12,
    leverancier_tarief: 0.08
  });
  const [hydrogenDisplaySettings, setHydrogenDisplaySettings] = useState({ 
    hydrogen_type: 'green', 
    show_retail: true 
  });

  // Moving average states voor vloeiende animaties - start met undefined om eerste data te gebruiken
  const [movingAverages, setMovingAverages] = useState({
    runtimePercentage: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false },
    currentPower: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false },
    hydrogenProduced: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false },
    estimatedValue: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false },
    energyConsumed: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false },
    energyCost: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false },
    grossProfit: { current: undefined, velocity: 0, history: [], hasFirstUpdate: false }
  });
  
  // Timestamp van laatste sync met API
  const [lastSyncTime, setLastSyncTime] = useState(moment());

  // Prijs berekening functies (zoals Planning pagina)
  const getHydrogenPriceField = () => {
    const typePrefix = `hydrogen_${hydrogenDisplaySettings.hydrogen_type}`;
    const suffix = hydrogenDisplaySettings.show_retail ? '_retail_price' : '_price';
    return `${typePrefix}${suffix}`;
  };

  const getHydrogenPrice = (data) => {
    const field = getHydrogenPriceField();
    return data[field];
  };

  const getPrice = (data) => {
    // Check eerst of we überhaupt prijs data hebben
    if (!data) {
      return 0;
    }
    
    if (electricitySettings.show_all_in) {
      // Gebruik all-in prijs direct uit database als beschikbaar
      if (data.price_all_in !== null && data.price_all_in !== undefined && data.price_all_in > 0) {
        return data.price_all_in;
      }
      
      // Fallback: bereken all-in prijs als database veld leeg is
      // Check verschillende mogelijke veldnamen (price vs electricityprice)
      const basePrice = data.price || data.electricityprice || 0;
      const netbeheerderTarief = data.price_netbeheerder_tarief || electricitySettings.netbeheerder_tarief || 0.12;
      const leverancierTarief = data.price_leverancier_tarief || electricitySettings.leverancier_tarief || 0.08;
      
      // Als we geen base price hebben, log een waarschuwing
      if (basePrice === 0) {
        // Geen logging
      }
      
      const calculatedPrice = basePrice + netbeheerderTarief + leverancierTarief;
      return calculatedPrice;
    } else {
      // Gebruik kale prijs (originele price veld)
      // Check verschillende mogelijke veldnamen
      const barePrice = data.price || data.electricityprice || 0;
      
      if (barePrice === 0) {
        // Geen logging
      }
      
      return barePrice;
    }
  };

  // Settings fetch functies
  const fetchElectricitySettings = async () => {
    try {
      const response = await get('/electricity-price/settings');
      if (response.data) {
        setElectricitySettings(response.data);
      }
    } catch (err) {
      console.error('Fout bij ophalen electricity price settings:', err);
      // Gebruik default settings bij fout
    }
  };

  const fetchHydrogenDisplaySettings = async () => {
    try {
      const response = await get('/hydrogen-price/display-settings');
      if (response.data) {
        setHydrogenDisplaySettings(response.data);
      }
    } catch (err) {
      console.error('Fout bij ophalen hydrogen display settings:', err);
      // Gebruik default settings bij fout
    }
  };

  // Fetch alle benodigde data
  const fetchDashboardData = async () => {
    try {
      // Alleen loading tonen bij handmatige refresh, niet bij auto-refresh
      if (!autoRefresh) {
        setLoading(true);
      }
      setError(null);

      // FIX: Gebruik stabiele dag boundaries in plaats van variabele 'now'
      const today = moment().tz('Europe/Amsterdam').startOf('day');
      const startOfDayCET = today.clone();
      const endOfDayCET = today.clone().endOf('day');
      
      // FIX: Correcte UTC conversie voor database queries
      const startOfDayUTC = startOfDayCET.clone().utc();
      const endOfDayUTC = endOfDayCET.clone().utc();

      // Parallel fetch van alle data inclusief settings en override status
      const [relaisResponse, scheduleResponse, configResponse, overrideResponse, latestRelaisResponse] = await Promise.all([
        get(`/relais-log?filter={"timestamp":{"gte":"${startOfDayUTC.toISOString()}","lte":"${endOfDayUTC.toISOString()}"}}&sort=["timestamp","ASC"]`),
        get(`/schedule?validfrom=${startOfDayUTC.toISOString()}&validto=${endOfDayUTC.toISOString()}`),
        get('/config'),
        get('/revpi/relay-status'), // Haal huidige override status op
        get('/relais-log?sort=["timestamp","DESC"]&range=[0,0]') // Haal alleen de meest recente relais log entry op
      ]);

      // Haal ook settings op
      await Promise.all([
        fetchElectricitySettings(),
        fetchHydrogenDisplaySettings()
      ]);

      // Verwerk responses
      if (relaisResponse.data?.data) {
        let relaisDataToSet = relaisResponse.data.data;
        
        // Als we een meer recente entry hebben, voeg deze toe
        if (latestRelaisResponse.data?.data?.[0]) {
          const latestEntry = latestRelaisResponse.data.data[0];
          const latestTimestamp = moment.utc(latestEntry.timestamp);
          
          // Check of deze entry nieuwer is dan wat we al hebben
          const lastExistingTimestamp = relaisDataToSet.length > 0 
            ? moment.utc(relaisDataToSet[relaisDataToSet.length - 1].timestamp)
            : null;
          
          if (!lastExistingTimestamp || latestTimestamp.isAfter(lastExistingTimestamp)) {
            // Voeg de meest recente entry toe als deze nog niet in de data zit
            relaisDataToSet = [...relaisDataToSet, latestEntry];
          }
        }
        
        setRelaisData(relaisDataToSet);
      }

      if (scheduleResponse.data?.blocks) {
        setScheduleData(scheduleResponse.data.blocks);
        // Maak price data van schedule data voor compatibiliteit
        const priceDataFromSchedule = scheduleResponse.data.blocks.map(block => ({
          timestamp: block.validfrom,
          price: block.price,
          price_all_in: block.price_all_in,
          price_netbeheerder_tarief: block.price_netbeheerder_tarief,
          price_leverancier_tarief: block.price_leverancier_tarief
        }));
        setPriceData(priceDataFromSchedule);
      }

      let configObject = {};
      if (configResponse.data?.configuration) {
        configResponse.data.configuration.forEach(setting => {
          configObject[setting.parameter] = setting.value;
        });
        setConfigData(configObject);
      }

      // Sla override status op
      if (overrideResponse.data) {
        setOverrideStatus(overrideResponse.data);
      }

      setLastRefresh(moment());

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Er is een fout opgetreden bij het ophalen van de gegevens');
    } finally {
      setLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [selectedEnvironment]);

  // Dynamisch refresh interval op basis van sessie tijd
  const calculateRefreshInterval = () => {
    const sessionDuration = moment().diff(lastUserInteraction, 'seconds');
    
    if (sessionDuration < 60) {
      return 5; // Eerste minuut: elke 5 seconden (was 1 sec)
    } else if (sessionDuration < 300) { // 5 minuten
      return 10; // 1-5 min: elke 10 seconden (was 5 sec)
    } else if (sessionDuration < 600) { // 10 minuten
      return 30; // 5-10 min: elke 30 seconden
    } else {
      return 60; // Na 10 min: elke minuut
    }
  };

  // Update refresh interval dynamisch
  useEffect(() => {
    const intervalChecker = setInterval(() => {
      const newInterval = calculateRefreshInterval();
      if (newInterval !== refreshInterval) {
        setRefreshInterval(newInterval);
      }
    }, 5000); // Check elke 5 seconden of interval moet worden aangepast

    return () => clearInterval(intervalChecker);
  }, [refreshInterval, lastUserInteraction]);

  // Auto-refresh functionaliteit
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDashboardData();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);
  
  // Reset last interaction time bij user activiteit
  const handleUserInteraction = useCallback(() => {
    setLastUserInteraction(moment());
    // Reset naar snelste interval bij user interactie
    if (refreshInterval !== 5) {
      setRefreshInterval(5);
    }
  }, [refreshInterval]);
  
  // Debounced versie voor mouse move
  const mouseMoveTimeoutRef = useRef(null);
  const handleMouseMove = useCallback(() => {
    if (mouseMoveTimeoutRef.current) {
      clearTimeout(mouseMoveTimeoutRef.current);
    }
    mouseMoveTimeoutRef.current = setTimeout(() => {
      handleUserInteraction();
    }, 1000); // Alleen trigger na 1 seconde geen beweging
  }, [handleUserInteraction]);

  // Bereken operationele KPI's (gebaseerd op RelaisLogKPIs logica)
  const operationalKPIs = useMemo(() => {
    if (!relaisData.length) {
      return {
        currentStatus: 'unknown',
        runtimeMinutes: 0,
        runtimePercentage: 0,
        switchCount: 0,
        lastReason: 'Geen data',
        currentPower: 0,
        plannedMinutes: 0
      };
    }

    // FIX: CONSISTENTE UTC/CET STRATEGIE
    // 1. Alle database timestamps zijn UTC
    // 2. Alle interne berekeningen in UTC  
    // 3. Alleen presentatie in CET

    // Sorteer op timestamp (UTC timestamps van database)
    const sortedData = [...relaisData].sort((a, b) => 
      moment.utc(a.timestamp).valueOf() - moment.utc(b.timestamp).valueOf()
    );

    // FIX: Gebruik volledige dag van vandaag (00:00 - 23:59 CET)
    const todayCET = moment().tz('Europe/Amsterdam');
    const startOfDayCET = todayCET.clone().startOf('day');
    const endOfDayCET = todayCET.clone().endOf('day');
    
    // Converteer naar UTC voor berekeningen
    const startOfDayUTC = startOfDayCET.clone().utc();
    const endOfDayUTC = endOfDayCET.clone().utc();
    const nowUTC = moment.utc();
    
    // Bepaal effectieve eindtijd (nu of einde van de dag, wat eerder is)
    const effectiveEndTimeUTC = nowUTC.isBefore(endOfDayUTC) ? nowUTC : endOfDayUTC;
    
    // Bereken geplande minuten uit schedule data
    let plannedMinutes = 0;
    if (scheduleData && scheduleData.length > 0) {
      scheduleData.forEach(block => {
        // Check of electrolyzer gepland is om te draaien in dit blok
        const isScheduledOn = block.manual === true || (block.manual === null && block.automatic === true);
        
        if (isScheduledOn) {
          // Check of dit blok binnen vandaag valt
          const blockStartUTC = moment.utc(block.validfrom);
          const blockEndUTC = moment.utc(block.validto);
          
          // Alleen tellen als het blok vandaag is
          if (blockStartUTC.isSameOrAfter(startOfDayUTC) && blockStartUTC.isBefore(endOfDayUTC)) {
            // Standaard blok is 15 minuten, maar check voor zekerheid
            const blockDuration = blockEndUTC.diff(blockStartUTC, 'minutes');
            plannedMinutes += blockDuration;
          }
        }
      });
    }
    
    // Bereken totale minuten van de dag voor runtime percentage (altijd hele dag)
    const totalMinutesForDay = effectiveEndTimeUTC.diff(startOfDayUTC, 'minutes');

    let runtimeMinutes = 0;
    let switchCount = 0;

    // FIX: Alle berekeningen in UTC
    for (let i = 0; i < sortedData.length - 1; i++) {
      const current = sortedData[i];
      const next = sortedData[i + 1];
      
      // Tel schakelingen
      if (current.status !== next.status) {
        switchCount++;
      }
      
      if (current.status === 'on') {
        const currentUTC = moment.utc(current.timestamp);
        const nextUTC = moment.utc(next.timestamp);
        const duration = nextUTC.diff(currentUTC, 'minutes');
        runtimeMinutes += duration;
      }
    }

    // FIX: Laatste periode berekening in UTC
    const lastRecord = sortedData[sortedData.length - 1];
    if (lastRecord.status === 'on') {
      const lastTimeUTC = moment.utc(lastRecord.timestamp);
      // Gebruik effectieve eindtijd (nu of einde van dag)
      const duration = effectiveEndTimeUTC.diff(lastTimeUTC, 'minutes');
      runtimeMinutes += duration;
    }

    // FIX: Bereken runtime percentage op basis van hele dag (24 uur)
    const power = parseFloat(configData.power) || 1000; // Gebruik juiste default
    const runtimePercentage = totalMinutesForDay > 0 ? (runtimeMinutes / totalMinutesForDay) * 100 : 0;

    // Simpel: aan = settings vermogen, uit = 0
    const currentPower = lastRecord?.status === 'on' ? power : 0;

    return {
      currentStatus: lastRecord?.status || 'unknown',
      lastReason: lastRecord?.reason || 'Geen data',
      runtimeMinutes,
      runtimePercentage,
      switchCount,
      currentPower,
      plannedMinutes
    };
  }, [relaisData, configData, scheduleData]);

  // Bereken energie kosten (gebaseerd op Planning logica)
  const energyKPIs = useMemo(() => {
    // FIX: Alleen berekenen als er werkelijk runtime is
    if (!operationalKPIs.runtimeMinutes || operationalKPIs.runtimeMinutes <= 0 || !scheduleData.length) {
      // Wel huidige prijs tonen als beschikbaar
      const currentPrice = scheduleData.length > 0 ? getPrice(scheduleData[scheduleData.length - 1]) : 0;
      
      return {
        currentPrice,
        avgPriceToday: 0,
        energyConsumed: 0,
        energyCostToday: 0
      };
    }

    // Vind het huidige tijdblok voor de juiste huidige prijs
    // BELANGRIJK: Schedule data is in UTC, dus vergelijk in UTC
    let currentPrice = 0;
    const nowUTC = moment.utc();
    const currentBlock = scheduleData.find(block => {
      const blockStartUTC = moment.utc(block.validfrom);
      const blockEndUTC = moment.utc(block.validto);
      return nowUTC.isSameOrAfter(blockStartUTC) && nowUTC.isBefore(blockEndUTC);
    });
    
    if (currentBlock) {
      currentPrice = getPrice(currentBlock);
    } else {
      // Fallback: gebruik laatste bekende prijs
      currentPrice = getPrice(scheduleData[scheduleData.length - 1]) || 0;
    }
    
    // Filter schedule data voor vandaag
    const todayStart = moment().tz('Europe/Amsterdam').startOf('day');
    const todayEnd = moment().tz('Europe/Amsterdam').endOf('day');
    
    const todaySchedule = scheduleData.filter(block => {
      const blockStart = moment(block.validfrom);
      return blockStart.isSameOrAfter(todayStart) && blockStart.isSameOrBefore(todayEnd);
    });
    
    // BELANGRIJK: Bereken gewogen gemiddelde alleen voor blokken waar electrolyzer gepland is om te draaien
    // EN alleen voor blokken tot nu toe (niet toekomstige blokken)
    let totalWeightedPrice = 0;
    let totalScheduledMinutes = 0;
    let totalWeightedPriceUpToNow = 0;
    let totalScheduledMinutesUpToNow = 0;
    
    const now = moment();
    
    todaySchedule.forEach(block => {
      // Check of electrolyzer gepland is om te draaien (manual=true of automatic=true)
      const isScheduledOn = block.manual === true || (block.manual === null && block.automatic === true);
      const blockEnd = moment(block.validto);
      const blockStart = moment(block.validfrom);
      
      if (isScheduledOn) {
        const blockPrice = getPrice(block);
        const blockDuration = 15; // Elk blok is 15 minuten
        
        totalWeightedPrice += blockPrice * blockDuration;
        totalScheduledMinutes += blockDuration;
        
        // Voor gemiddelde tot nu toe: alleen blokken die al (deels) zijn geweest
        if (blockStart.isBefore(now)) {
          // Als blok al voorbij is, tel volledig mee
          if (blockEnd.isBefore(now)) {
            totalWeightedPriceUpToNow += blockPrice * blockDuration;
            totalScheduledMinutesUpToNow += blockDuration;
          } else {
            // Als blok deels voorbij is, bereken hoeveel minuten tot nu
            const minutesElapsed = now.diff(blockStart, 'minutes');
            totalWeightedPriceUpToNow += blockPrice * minutesElapsed;
            totalScheduledMinutesUpToNow += minutesElapsed;
          }
        }
      }
    });
    
    // Bereken gewogen gemiddelde prijs tot nu toe
    let avgPriceToday = 0;
    if (totalScheduledMinutesUpToNow > 0) {
      avgPriceToday = totalWeightedPriceUpToNow / totalScheduledMinutesUpToNow;
    } else if (totalScheduledMinutes > 0) {
      // Als electrolyzer nog niet heeft gedraaid vandaag, maar wel gepland staat
      // gebruik het gemiddelde van de geplande uren
      avgPriceToday = totalWeightedPrice / totalScheduledMinutes;
    } else {
      // Fallback: als geen geplande blokken, gebruik gemiddelde van alle blokken tot nu toe
      const blocksUpToNow = todaySchedule.filter(block => moment(block.validfrom).isBefore(now));
      const totalPrice = blocksUpToNow.reduce((sum, block) => sum + getPrice(block), 0);
      avgPriceToday = blocksUpToNow.length > 0 ? totalPrice / blocksUpToNow.length : currentPrice;
    }

    // Energie verbruik: runtime (uren) × vermogen (kW)
    const power = parseFloat(configData.power) || 1000;
    const runtimeHours = operationalKPIs.runtimeMinutes / 60;
    const energyConsumed = runtimeHours * power;

    // Kosten: verbruik × gewogen gemiddelde prijs
    const energyCostToday = energyConsumed * avgPriceToday;

    return {
      currentPrice,
      avgPriceToday,
      energyConsumed,
      energyCostToday
    };
  }, [scheduleData, operationalKPIs, configData, electricitySettings]);

  // Bereken waterstof productie (gebaseerd op SimulationPage logica)
  const hydrogenKPIs = useMemo(() => {
    // FIX: Alleen berekenen als er werkelijk runtime en schedule data is
    if (!operationalKPIs.runtimeMinutes || operationalKPIs.runtimeMinutes <= 0 || !scheduleData.length) {
      return {
        hydrogenProduced: 0,
        estimatedValue: 0,
        currentHydrogenPrice: 0,
        avgHydrogenPrice: 0
      };
    }

    // Parameters uit config of defaults (zoals SettingsPage.jsx)
    const efficiency = parseFloat(configData.efficiency) / 100 || 0.611; // 61.1% default
    const energyPerKgH2 = parseFloat(configData.energy_per_kg_kwh) || 13.33; // kWh per kg H2 (juiste parameter naam)
    const power = parseFloat(configData.power) || 1000; // 1000 kW default

    const runtimeHours = operationalKPIs.runtimeMinutes / 60;
    const totalConsumption = power * runtimeHours; // kWh
    const energyForHydrogen = totalConsumption * efficiency;
    const hydrogenProduced = energyForHydrogen / energyPerKgH2;

    // FIX: Gebruik juiste waterstof prijs logica zoals Planning pagina
    // Filter alleen schedule blocks met waterstof prijs data
    const hydrogenData = scheduleData.filter(block => {
      const hydrogenPrice = getHydrogenPrice(block);
      return hydrogenPrice !== null && hydrogenPrice !== undefined;
    });
    
    let avgHydrogenPrice = 0;
    let currentHydrogenPrice = 0;
    
    if (hydrogenData.length > 0) {
      // Bereken gemiddelde prijs
      const totalHydrogenPrice = hydrogenData.reduce((sum, block) => sum + getHydrogenPrice(block), 0);
      avgHydrogenPrice = totalHydrogenPrice / hydrogenData.length;
      
      // Vind huidige waterstofprijs (meest recente block)
      const now = moment.utc();
      const currentBlock = hydrogenData.find(block => {
        const blockStart = moment.utc(block.validfrom);
        const blockEnd = moment.utc(block.validto);
        return now.isSameOrAfter(blockStart) && now.isBefore(blockEnd);
      });
      
      if (currentBlock) {
        currentHydrogenPrice = getHydrogenPrice(currentBlock);
      } else {
        // Als geen huidige block, gebruik de laatste bekende prijs
        const sortedData = [...hydrogenData].sort((a, b) => 
          moment.utc(b.validfrom).valueOf() - moment.utc(a.validfrom).valueOf()
        );
        currentHydrogenPrice = getHydrogenPrice(sortedData[0]);
      }
    } else {
      // Fallback naar conservatieve schatting als geen prijs data
      avgHydrogenPrice = 6.50; // €/kg rough estimate
      currentHydrogenPrice = 6.50;
    }

    // Gebruik huidige prijs voor waarde berekening
    const estimatedValue = hydrogenProduced * currentHydrogenPrice;

    return {
      hydrogenProduced,
      estimatedValue,
      currentHydrogenPrice,
      avgHydrogenPrice
    };
  }, [operationalKPIs, configData, scheduleData, hydrogenDisplaySettings]);

  // Bereken planning KPI's (gebaseerd op Schedule component logica)
  const planningKPIs = useMemo(() => {
    if (!scheduleData.length) {
      return {
        plannedHoursToday: 0,
        adherencePercentage: 0,
        nextStart: null,
        nextStop: null
      };
    }

    // FIX: CONSISTENTE UTC/CET STRATEGIE voor planning
    // 1. Schedule timestamps zijn UTC van backend
    // 2. Alle berekeningen in UTC
    // 3. Alleen presentatie in CET

    const nowUTC = moment.utc();
    const todayUTC = moment.utc().startOf('day');
    const endOfDayUTC = todayUTC.clone().endOf('day');

    // Filter schedule voor vandaag (UTC)
    const todaySchedule = scheduleData.filter(block => {
      const blockStartUTC = moment.utc(block.validfrom);
      return blockStartUTC.isSameOrAfter(todayUTC) && blockStartUTC.isSameOrBefore(endOfDayUTC);
    });

    // Bereken geplande uren (waar manual=true of automatic=true) - UTC berekening
    let plannedMinutes = 0;
    let nextStart = null;
    let nextStop = null;

    // Bepaal of we naar start of stop moeten zoeken op basis van huidige status
    const isCurrentlyRunning = operationalKPIs.currentStatus === 'on';

    todaySchedule.forEach(block => {
      const isPlannedOn = block.manual === true || (block.manual === null && block.automatic);
      const blockStartUTC = moment.utc(block.validfrom);
      const blockEndUTC = moment.utc(block.validto);
      
      if (isPlannedOn) {
        const duration = blockEndUTC.diff(blockStartUTC, 'minutes');
        plannedMinutes += duration;
      }

      // Zoek volgende start/stop op basis van huidige status
      if (blockStartUTC.isAfter(nowUTC)) {
        if (isCurrentlyRunning) {
          // Als electrolyzer draait, zoek alleen naar volgende stop (off periode)
          if (!isPlannedOn && !nextStop) {
            nextStop = blockStartUTC.tz('Europe/Amsterdam'); // Converteer naar CET voor presentatie
          }
        } else {
          // Als electrolyzer uit staat, zoek alleen naar volgende start (on periode)
          if (isPlannedOn && !nextStart) {
            nextStart = blockStartUTC.tz('Europe/Amsterdam'); // Converteer naar CET voor presentatie
          }
        }
      }
    });

    const plannedHoursToday = plannedMinutes / 60;
    const actualHours = operationalKPIs.runtimeMinutes / 60;
    const adherencePercentage = plannedHoursToday > 0 ? (actualHours / plannedHoursToday) * 100 : 0;

    return {
      plannedHoursToday,
      adherencePercentage,
      nextStart,
      nextStop
    };
  }, [scheduleData, operationalKPIs]);

  // Bereken extra business metrics
  const businessMetrics = useMemo(() => {
    const currentRevenue = hydrogenKPIs.estimatedValue || 0;
    const currentCosts = energyKPIs.energyCostToday || 0;
    const grossProfit = currentRevenue - currentCosts;
    const profitMargin = currentRevenue > 0 ? (grossProfit / currentRevenue) * 100 : 0;
    
    // Eenvoudige trend berekening (zou in productie gebaseerd zijn op historische data)
    const isRunning = operationalKPIs.currentStatus === 'on';
    const efficiency = planningKPIs.adherencePercentage;
    
    return {
      grossProfit,
      profitMargin,
      isRunning,
      isProfitable: grossProfit > 0,
      efficiency,
      revenuePerHour: operationalKPIs.runtimeMinutes > 0 ? (currentRevenue / (operationalKPIs.runtimeMinutes / 60)) : 0,
      costPerKg: hydrogenKPIs.hydrogenProduced > 0 ? (currentCosts / hydrogenKPIs.hydrogenProduced) : 0
    };
  }, [hydrogenKPIs, energyKPIs, operationalKPIs, planningKPIs, electricitySettings, hydrogenDisplaySettings]);

  // Update moving averages wanneer KPIs veranderen
  useEffect(() => {
    const timeDiff = moment().diff(lastSyncTime, 'seconds');
    if (timeDiff === 0) return; // Voorkom divisie door nul

    setMovingAverages(prev => {
      const newAverages = { ...prev };
      
      // Helper functie om moving average bij te werken
      const updateAverage = (key, newValue) => {
        const oldValue = prev[key].current;
        const hasFirstUpdate = prev[key].hasFirstUpdate;
        
        // Bereken velocity op basis van fysieke eigenschappen
        let calculatedVelocity = 0;
        
        // Als electrolyzer draait, bereken exacte velocities
        if (operationalKPIs.currentStatus === 'on') {
          const power = parseFloat(configData.power) || 1000; // kW
          const efficiency = parseFloat(configData.efficiency) / 100 || 0.611;
          const energyPerKg = parseFloat(configData.energy_per_kg_kwh) || 13.33;
          
          switch(key) {
            case 'runtimePercentage':
              // Runtime groeit met 100% per 24 uur = 100/86400 per seconde
              calculatedVelocity = 100 / 86400;
              break;
              
            case 'currentPower':
              // Power verandert niet tijdens runtime
              calculatedVelocity = 0;
              break;
              
            case 'energyConsumed':
              // Energie verbruik: power (kW) / 3600 = kWh per seconde
              calculatedVelocity = power / 3600;
              break;
              
            case 'hydrogenProduced':
              // H2 productie: (power * efficiency) / energyPerKg / 3600 kg per seconde
              calculatedVelocity = (power * efficiency) / energyPerKg / 3600;
              break;
              
            case 'energyCost':
              // Kosten: energie verbruik * huidige elektriciteitsprijs
              const currentPrice = energyKPIs.currentPrice || 0.20;
              calculatedVelocity = (power / 3600) * currentPrice;
              break;
              
            case 'estimatedValue':
              // Opbrengst: H2 productie * huidige waterstof prijs
              const h2Price = hydrogenKPIs.currentHydrogenPrice || 6.50;
              const h2PerSecond = (power * efficiency) / energyPerKg / 3600;
              calculatedVelocity = h2PerSecond * h2Price;
              break;
              
            case 'grossProfit':
              // Winst: opbrengst - kosten
              const revenue = ((power * efficiency) / energyPerKg / 3600) * (hydrogenKPIs.currentHydrogenPrice || 6.50);
              const costs = (power / 3600) * (energyKPIs.currentPrice || 0.20);
              calculatedVelocity = revenue - costs;
              break;
          }
        } else {
          // Electrolyzer staat uit - alleen currentPower kan direct naar 0
          if (key === 'currentPower' && oldValue !== 0) {
            calculatedVelocity = 0; // Direct naar 0
          } else {
            calculatedVelocity = 0; // Alle andere counters stoppen met groeien
          }
        }
        
        // Bij eerste update of als oldValue undefined is
        if (!hasFirstUpdate || oldValue === undefined) {
          return {
            current: newValue,
            velocity: calculatedVelocity,
            history: [newValue],
            fullHistory: [{ value: newValue, time: moment() }],
            smoothedVelocity: calculatedVelocity,
            hasFirstUpdate: true
          };
        }
        
                const instantVelocity = (newValue - oldValue) / timeDiff; // Verandering per seconde
        
        // Bewaar laatste 10 waarden voor betere smoothing
        const prevHistory = prev[key].fullHistory || prev[key].history || [];
        const historyItems = Array.isArray(prevHistory) && prevHistory.length > 0 && typeof prevHistory[0] === 'object' 
          ? prevHistory 
          : prevHistory.map((val, idx) => ({ value: val, time: moment().subtract(prevHistory.length - idx, 'seconds') }));
        const history = [...historyItems, { value: newValue, time: moment() }].slice(-10);
        
        // Mix calculated velocity met correctie voor afwijking
        let finalVelocity = calculatedVelocity;
        
        // Als er een significant verschil is tussen verwacht en werkelijk
        const expectedValue = oldValue + (calculatedVelocity * timeDiff);
        const deviation = newValue - expectedValue;
        
        if (Math.abs(deviation) > 0.01) {
          // Pas velocity aan om binnen redelijke tijd te corrigeren
          const correctionTime = 30; // Corrigeer over 30 seconden
          const correctionVelocity = deviation / correctionTime;
          
          // Mix calculated velocity met correctie (90% calculated, 10% correctie)
          finalVelocity = calculatedVelocity * 0.9 + correctionVelocity * 0.1;
        }
        
        return {
          current: newValue,
          velocity: instantVelocity,
          history: history.map(h => h.value), // Voor backwards compatibility
          fullHistory: history,
          smoothedVelocity: finalVelocity,
          hasFirstUpdate: true
        };
      };
      
      // Update alle KPIs
      newAverages.runtimePercentage = updateAverage('runtimePercentage', operationalKPIs.runtimePercentage);
      newAverages.currentPower = updateAverage('currentPower', operationalKPIs.currentPower);
      newAverages.hydrogenProduced = updateAverage('hydrogenProduced', hydrogenKPIs.hydrogenProduced);
      newAverages.estimatedValue = updateAverage('estimatedValue', hydrogenKPIs.estimatedValue);
      newAverages.energyConsumed = updateAverage('energyConsumed', energyKPIs.energyConsumed);
      newAverages.energyCost = updateAverage('energyCost', energyKPIs.energyCostToday);
      newAverages.grossProfit = updateAverage('grossProfit', businessMetrics.grossProfit);
      
      return newAverages;
    });
    
    setLastSyncTime(moment());
  }, [operationalKPIs, hydrogenKPIs, energyKPIs, businessMetrics, configData]);

  const refreshData = () => {
    handleUserInteraction(); // Register user interaction
    fetchDashboardData();
  };

  // Nederlandse bedrag formatting
  const formatEuro = (amount) => {
    return `€ ${amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Stroomprijs formatting (4 decimalen)
  const formatPrice = (price) => {
    return `€ ${price.toLocaleString('nl-NL', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on': return 'success';
      case 'off': return 'default';
      case 'error': return 'error';
      default: return 'warning';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'on': return <CheckCircle color="success" />;
      case 'off': return <PowerSettingsNew color="disabled" />;
      case 'error': return <Error color="error" />;
      default: return <Warning color="warning" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'on': return 'Draait';
      case 'off': return 'Gestopt';
      case 'error': return 'Fout';
      default: return 'Onbekend';
    }
  };

  if (isInitialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box onClick={handleUserInteraction} onMouseMove={handleMouseMove}>
      {/* Moderne Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 3,
        p: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 2,
        color: 'white'
      }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Live Dashboard
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Realtime monitoring van uw waterstofproductie
          </Typography>
          <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
            Laatste gegevens: {lastRefresh.format('DD-MM-YYYY HH:mm:ss')}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {/* Auto-refresh Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={autoRefresh} 
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size="small"
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: 'white' }}>
                  Auto-refresh ({refreshInterval}s)
                </Typography>
              }
            />
            <Tooltip title="Direct verversen van alle gegevens">
              <IconButton 
                onClick={refreshData} 
                disabled={loading}
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', position: 'relative' }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <Refresh />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Live Status Indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box 
              sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: autoRefresh ? '#4caf50' : '#ff9800',
                animation: autoRefresh ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }} 
            />
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
              {autoRefresh ? 'Live' : 'Gepauzeerd'}
            </Typography>
          </Box>
          
          {/* Refresh interval info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.7, fontSize: '0.65rem' }}>
              Verversingsfrequentie:
            </Typography>
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.6, fontSize: '0.6rem' }}>
              {refreshInterval === 5 && 'Actief gebruik: elke 5 seconden'}
              {refreshInterval === 10 && 'Recent actief: elke 10 sec'}
              {refreshInterval === 30 && 'Monitoring: elke 30 sec'}
              {refreshInterval === 60 && 'Inactief: elke minuut'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Environment Info */}
      {isMultiTenantMode() && selectedEnvironment && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            Actieve omgeving: <strong>{selectedEnvironment.name}</strong>
          </Typography>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quick Status Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* System Status - COMPACT */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            background: operationalKPIs.currentStatus === 'on' 
              ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
              : 'linear-gradient(135deg, #757575 0%, #616161 100%)',
            color: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ pb: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Systeemstatus
                    </Typography>
                    <Tooltip title="Actuele status van de electrolyzer met reden voor huidige toestand. Badge toont aantal statuswijzigingen vandaag." arrow>
                      <Info sx={{ fontSize: 14, opacity: 0.7 }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {operationalKPIs.currentStatus === 'on' ? 
                      <PlayArrow sx={{ fontSize: 18 }} /> : 
                      <Pause sx={{ fontSize: 18 }} />
                    }
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {getStatusText(operationalKPIs.currentStatus)}
                    </Typography>
                  </Box>
                  
                  {/* Override Status */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Modus: {
                        overrideStatus?.workingStatus === 'auto' ? 'Volgens planning' :
                        overrideStatus?.workingStatus === 'on' ? 'Direct aan' :
                        overrideStatus?.workingStatus === 'off' ? 'Direct uit' :
                        'Onbekend'
                      }
                    </Typography>
                  </Box>
                </Box>
                <Badge 
                  badgeContent={operationalKPIs.switchCount} 
                  color="secondary"
                  sx={{ '& .MuiBadge-badge': { bgcolor: 'rgba(255,255,255,0.8)', color: 'black' } }}
                >
                  {getStatusIcon(operationalKPIs.currentStatus)}
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Power - COMPACT */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            background: operationalKPIs.currentPower > 0 
              ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
              : 'linear-gradient(135deg, #9E9E9E 0%, #757575 100%)',
            color: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ pb: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Huidig vermogen
                    </Typography>
                    <Tooltip title="Actueel opgenomen elektrisch vermogen. Wordt bepaald door de vermogensinstelling wanneer actief, 0 kW wanneer inactief." arrow>
                      <Info sx={{ fontSize: 14, opacity: 0.7 }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    <ExtrapolatedNumber 
                      movingAverage={movingAverages.currentPower} 
                      fallbackValue={operationalKPIs.currentPower}
                      decimals={0} 
                      suffix=" kW" 
                    />
                  </Typography>
                </Box>
                <Factory sx={{ fontSize: 30, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Runtime Compact */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ pb: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Runtime Vandaag
                </Typography>
                <Tooltip title={`Percentage van de verstreken tijd vandaag dat de electrolyzer heeft gedraaid. ${Math.floor(operationalKPIs.runtimeMinutes / 60)}u ${operationalKPIs.runtimeMinutes % 60}m tot nu toe.`} arrow>
                  <Info sx={{ fontSize: 14, opacity: 0.5 }} />
                </Tooltip>
              </Box>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                <ExtrapolatedNumber 
                  movingAverage={movingAverages.runtimePercentage} 
                  fallbackValue={operationalKPIs.runtimePercentage}
                  format="percentage" 
                  decimals={1} 
                />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.floor(operationalKPIs.runtimeMinutes / 60)}u {operationalKPIs.runtimeMinutes % 60}m
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Profitability Compact */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            background: businessMetrics.isProfitable 
              ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' 
              : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
            color: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ pb: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Dagresultaat
                    </Typography>
                    <Tooltip title="Financieel resultaat vandaag: opbrengst waterstofproductie minus energiekosten. Groen = winstgevend, rood = verliesgevend." arrow>
                      <Info sx={{ fontSize: 14, opacity: 0.7 }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    <ExtrapolatedNumber 
                      movingAverage={movingAverages.grossProfit}
                      fallbackValue={businessMetrics.grossProfit}
                      format="currency" 
                      decimals={2} 
                      prefix={businessMetrics.grossProfit >= 0 ? '+' : ''} 
                      canDecrease={true}
                    />
                  </Typography>
                </Box>
                {businessMetrics.isProfitable ? 
                  <TrendingUp sx={{ fontSize: 30, opacity: 0.8 }} /> :
                  <TrendingDown sx={{ fontSize: 30, opacity: 0.8 }} />
                }
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Business KPI's - Moderne Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Production & Revenue */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalGasStation color="warning" />
                <Typography variant="h6">
                  Productie & Opbrengst Vandaag
                </Typography>
                <Tooltip title="Waterstofproductie berekend op basis van runtime, vermogen en efficiency. Geschatte waarde volgens actuele prijzen (type en prijsmodel volgens instellingen)." arrow>
                  <Info sx={{ fontSize: 16, opacity: 0.5 }} />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                      <ExtrapolatedNumber 
                        movingAverage={movingAverages.hydrogenProduced} 
                        fallbackValue={hydrogenKPIs.hydrogenProduced}
                        decimals={1} 
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      kg Waterstof
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Huidige H₂: <AnimatedNumber value={hydrogenKPIs.currentHydrogenPrice} format="currency" decimals={2} suffix="/kg" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                      <ExtrapolatedNumber 
                        movingAverage={movingAverages.estimatedValue} 
                        fallbackValue={hydrogenKPIs.estimatedValue}
                        format="currency" 
                        decimals={2} 
                        canDecrease={true}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Geschatte opbrengst
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Opbrengstsnelheid: <AnimatedNumber value={businessMetrics.revenuePerHour} format="currency" decimals={2} suffix="/uur" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Waterstof prijs info */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Waterstofprijzen vandaag:
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    Actueel: <AnimatedNumber value={hydrogenKPIs.currentHydrogenPrice} format="currency" decimals={2} suffix="/kg" />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Gemiddeld: <AnimatedNumber value={hydrogenKPIs.avgHydrogenPrice} format="currency" decimals={2} suffix="/kg" />
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Energy & Costs */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShowChart color="primary" />
                <Typography variant="h6">
                  Energie & Kosten Vandaag
                </Typography>
                <Tooltip title="Totaal energieverbruik en kosten berekend met gewogen gemiddelde stroomprijs. Prijstype volgens instellingen (all-in of kaal). 'Nu' toont prijs van huidig tijdblok." arrow>
                  <Info sx={{ fontSize: 16, opacity: 0.5 }} />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                      <ExtrapolatedNumber 
                        movingAverage={movingAverages.energyConsumed} 
                        fallbackValue={energyKPIs.energyConsumed}
                        decimals={0} 
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      kWh Verbruikt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      @<AnimatedNumber value={energyKPIs.avgPriceToday} format="currency" decimals={4} suffix="/kWh" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h3" color="error.main" sx={{ fontWeight: 700 }}>
                      <ExtrapolatedNumber 
                        movingAverage={movingAverages.energyCost} 
                        fallbackValue={energyKPIs.energyCostToday}
                        format="currency" 
                        decimals={2} 
                        canDecrease={true}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Totale kosten
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Actuele prijs: <AnimatedNumber value={energyKPIs.currentPrice} format="currency" decimals={4} suffix="/kWh" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Kostenverdeling */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Verbruik per kg H₂:
                  </Typography>
                  <Tooltip title="Werkelijk energieverbruik per kg waterstof. Hangt af van efficiency instelling. Lager is beter voor de business case." arrow>
                    <Info sx={{ fontSize: 12, opacity: 0.5 }} />
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <AnimatedNumber 
                    value={hydrogenKPIs.hydrogenProduced > 0 ? (energyKPIs.energyConsumed / hydrogenKPIs.hydrogenProduced) : 0} 
                    decimals={1} 
                    suffix=" kWh/kg H₂" 
                  />
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Kostprijs per kg:
                  </Typography>
                  <Tooltip title="Energiekosten per kg waterstof. Belangrijk voor winstgevendheid: moet lager zijn dan verkoopprijs voor positief resultaat." arrow>
                    <Info sx={{ fontSize: 12, opacity: 0.5 }} />
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <AnimatedNumber value={businessMetrics.costPerKg} format="currency" decimals={2} suffix="/kg H₂" />
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Cards */}
      <Grid container spacing={3}>
        {/* Planning Status - COMPACT & MODERN */}
        <Grid item xs={12}>
          <Card elevation={2} sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule color="primary" />
                  <Typography variant="h6">
                    Planning & Efficiency Overzicht
                  </Typography>
                  <Tooltip title="Planning naleving: percentage werkelijke runtime t.o.v. geplande uren. Groen ≥90%, oranje ≥70%, rood <70%. Volgende actie alleen zichtbaar in automodus." arrow>
                    <Info sx={{ fontSize: 16, opacity: 0.5 }} />
                  </Tooltip>
                </Box>
                
                {/* Quick Stats Row */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                      <AnimatedNumber value={planningKPIs.plannedHoursToday} decimals={1} suffix="u" />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Gepland
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                      <AnimatedNumber value={operationalKPIs.runtimeMinutes / 60} decimals={1} suffix="u" />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Werkelijk
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        color: planningKPIs.adherencePercentage >= 90 ? 'success.main' : 
                               planningKPIs.adherencePercentage >= 70 ? 'warning.main' : 'error.main'
                      }}
                    >
                      <AnimatedNumber value={planningKPIs.adherencePercentage} format="percentage" decimals={0} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Adherentie
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Progress Visualization */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Naleving van planning
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <AnimatedNumber value={planningKPIs.adherencePercentage} format="percentage" decimals={1} />
                  </Typography>
                </Box>
                <AnimatedProgress 
                  variant="determinate"
                  value={Math.min(planningKPIs.adherencePercentage, 100)} 
                  sx={{ 
                    height: 12, 
                    borderRadius: 6,
                    bgcolor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6,
                      bgcolor: planningKPIs.adherencePercentage >= 90 ? '#4caf50' : 
                               planningKPIs.adherencePercentage >= 70 ? '#ff9800' : '#f44336'
                    }
                  }}
                />
              </Box>

              {/* Next Actions - Toon alleen relevante actie op basis van huidige status */}
              {/* Alleen tonen als electrolyzer in auto mode staat */}
              {overrideStatus?.workingStatus === 'auto' && (planningKPIs.nextStart || planningKPIs.nextStop) && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  {operationalKPIs.currentStatus === 'off' && planningKPIs.nextStart && (
                    <Chip 
                      icon={<PlayArrow />}
                      label={`Volgende start: ${typeof planningKPIs.nextStart === 'object' ? planningKPIs.nextStart.format('HH:mm') : planningKPIs.nextStart}`}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {operationalKPIs.currentStatus === 'on' && planningKPIs.nextStop && (
                    <Chip 
                      icon={<Pause />}
                      label={`Volgende stop: ${typeof planningKPIs.nextStop === 'object' ? planningKPIs.nextStop.format('HH:mm') : planningKPIs.nextStop}`}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              )}
              
              {/* Toon modus informatie als niet in auto */}
              {overrideStatus?.workingStatus !== 'auto' && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Chip 
                    icon={<Engineering />}
                    label={`Handmatige modus: ${overrideStatus?.workingStatus === 'on' ? 'geforceerd AAN' : overrideStatus?.workingStatus === 'off' ? 'geforceerd UIT' : 'onbekend'}`}
                    color={overrideStatus?.workingStatus === 'on' ? 'success' : overrideStatus?.workingStatus === 'off' ? 'error' : 'default'}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    Planning wordt genegeerd in handmatige modus
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage; 